import { notifications } from "@mantine/notifications"
import { AlertCircle, CheckCircle } from "lucide-react"
import { createElement } from "react"
import { create } from "zustand"

import type {
  DepartmentDeactivationConflictError,
  DepartmentUnassignConflictError,
} from "@/services/departments.service"
import { departmentsService } from "@/services/departments.service"
import type {
  Department,
  DepartmentDeactivationImpact,
  DepartmentFormData,
  DepartmentSettingsFormData,
  DepartmentUnassignConflict,
  InstanceAction,
} from "@/types"

export type DepartmentUnassignActionResult =
  | { success: true }
  | { success: false; conflict: DepartmentUnassignConflict }
  | { success: false }

export type DepartmentDeactivationActionResult =
  | { success: true }
  | { success: false; impact: DepartmentDeactivationImpact }
  | { success: false }

interface DepartmentsState {
  // State
  departments: Department[]
  isLoading: boolean
  error: string | null
  selectedDepartment: Department | null

  // Actions
  fetchDepartments: () => Promise<void>
  getDepartmentById: (id: number) => Promise<void>
  createDepartment: (formData: DepartmentFormData) => Promise<Department | null>
  updateDepartment: (id: number, formData: DepartmentFormData, silent?: boolean) => Promise<Department | null>
  /** Settings-only update (PATCH) that never sends `admin` or `is_active`; used by a department admin. */
  updateDepartmentSettings: (id: number, settings: DepartmentSettingsFormData) => Promise<Department | null>
  deleteDepartment: (
    id: number,
    confirm?: boolean,
    instanceAction?: InstanceAction
  ) => Promise<DepartmentUnassignActionResult>
  deactivateDepartment: (id: number, confirm?: boolean) => Promise<DepartmentDeactivationActionResult>
  activateDepartment: (id: number) => Promise<boolean>
  assignAdmin: (departmentId: number, memberId: number) => Promise<Department | null>
  removeAdmin: (departmentId: number, adminId: number) => Promise<Department | null>
  setSelectedDepartment: (department: Department | null) => void
  clearError: () => void
  reset: () => void
}

const initialState = {
  departments: [],
  isLoading: false,
  error: null,
  selectedDepartment: null,
}

export const useDepartmentsStore = create<DepartmentsState>((set, get) => ({
  ...initialState,

  /**
   * Fetch all departments
   */
  fetchDepartments: async () => {
    set({ isLoading: true, error: null })
    try {
      const departments = await departmentsService.getAllDepartments()
      set({ departments, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch departments"
      set({ error: errorMessage, isLoading: false })
      notifications.show({
        title: "Error Loading Departments",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
    }
  },

  /**
   * Get department by ID
   */
  getDepartmentById: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      const department = await departmentsService.getDepartmentById(id)
      set({ selectedDepartment: department, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch department details"
      set({ error: errorMessage, isLoading: false })
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
    }
  },

  /**
   * Create a new department
   */
  createDepartment: async (formData: DepartmentFormData) => {
    set({ isLoading: true, error: null })
    try {
      const newDepartment = await departmentsService.createDepartment(formData)

      // Add to departments list
      set((state) => ({
        departments: [...state.departments, newDepartment],
        selectedDepartment: newDepartment,
        isLoading: false,
      }))

      // Show success notification
      notifications.show({
        title: "Department Created",
        message: `${newDepartment.name} has been successfully created`,
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return newDepartment
    } catch (error: any) {
      const errorMessage = error.message || "Failed to create department. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Creation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Update an existing department
   */
  updateDepartment: async (id: number, formData: DepartmentFormData, silent = false) => {
    set({ isLoading: true, error: null })
    try {
      const updatedDepartment = await departmentsService.updateDepartment(id, formData)

      // Update in departments list
      set((state) => ({
        departments: state.departments.map((d) => (d.id === id ? updatedDepartment : d)),
        selectedDepartment: updatedDepartment,
        isLoading: false,
      }))

      // Show success notification (unless silent mode)
      if (!silent) {
        notifications.show({
          title: "Department Updated",
          message: `${updatedDepartment.name} has been successfully updated`,
          color: "blue",
          icon: createElement(CheckCircle, { size: 18 }),
          autoClose: 4000,
          withCloseButton: true,
        })
      }

      return updatedDepartment
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update department. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Update Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Update only a department's settings (never `admin` or `is_active`). This is the
   * update a department admin uses on their own department.
   */
  updateDepartmentSettings: async (id: number, settings: DepartmentSettingsFormData) => {
    set({ isLoading: true, error: null })
    try {
      const updatedDepartment = await departmentsService.updateDepartmentSettings(id, settings)

      set((state) => ({
        departments: state.departments.map((d) => (d.id === id ? updatedDepartment : d)),
        selectedDepartment: updatedDepartment,
        isLoading: false,
      }))

      notifications.show({
        title: "Department Updated",
        message: `${updatedDepartment.name} has been successfully updated`,
        color: "blue",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return updatedDepartment
    } catch (error: unknown) {
      const errorMessage = (error instanceof Error && error.message) || "Failed to update department. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Update Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Delete a department. Without `confirm`, a populated department returns
   * a conflict with dry-run counts instead of an error — the caller should
   * show those counts + an instance_action choice, then call again with
   * confirm=true.
   */
  deleteDepartment: async (id: number, confirm = false, instanceAction: InstanceAction = "leave") => {
    const department = get().departments.find((d) => d.id === id)
    const departmentName = department ? department.name : "Department"

    set({ isLoading: true, error: null })
    try {
      const result = await departmentsService.deleteDepartment(id, confirm, instanceAction)

      // Remove from departments list
      set((state) => ({
        departments: state.departments.filter((d) => d.id !== id),
        selectedDepartment: null,
        isLoading: false,
      }))

      notifications.show({
        title: "Department Deleted",
        message: result.message || `${departmentName} has been successfully deleted`,
        color: "red",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return { success: true as const }
    } catch (error: any) {
      set({ isLoading: false })

      const conflict = (error as DepartmentUnassignConflictError).conflict
      if (conflict) {
        // Expected dry-run response, not a failure — let the caller decide what to show.
        return { success: false as const, conflict }
      }

      const errorMessage = error.message || "Failed to delete department. Please try again."
      set({ error: errorMessage })

      notifications.show({
        title: "Deletion Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return { success: false as const }
    }
  },

  /**
   * Deactivate (freeze) a department. Same dry-run/confirm pattern as
   * deleteDepartment, but nothing is unassigned: the admin, members and
   * instances stay put and only is_active flips to false.
   */
  deactivateDepartment: async (id: number, confirm = false) => {
    const department = get().departments.find((d) => d.id === id)
    const departmentName = department ? department.name : "Department"

    set({ isLoading: true, error: null })
    try {
      const result = await departmentsService.deactivateDepartment(id, confirm)

      set((state) => {
        // Deactivation is a freeze: the admin stays assigned.
        const applyDeactivated = (d: Department) => ({ ...d, is_active: false })
        return {
          departments: state.departments.map((d) => (d.id === id ? applyDeactivated(d) : d)),
          selectedDepartment:
            state.selectedDepartment?.id === id ? applyDeactivated(state.selectedDepartment) : state.selectedDepartment,
          isLoading: false,
        }
      })

      notifications.show({
        title: "Department Deactivated",
        message: result.message || `${departmentName} has been deactivated`,
        color: "orange",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      // SSH revocation is best-effort in AWS: never report full success when
      // some rules could not be removed.
      const sshWarnings: string[] = []
      if (result.ssh_rules_failed > 0) {
        sshWarnings.push(`${result.ssh_rules_failed} SSH rule(s) could not be revoked in AWS.`)
      }
      if (result.ssh_rules_skipped_shared > 0) {
        sshWarnings.push(
          `${result.ssh_rules_skipped_shared} SSH rule(s) were not removed in AWS because another active department uses the same security-group rule.`
        )
      }
      if (sshWarnings.length > 0) {
        notifications.show({
          title: "SSH access may still be open",
          message: sshWarnings.join(" "),
          color: "yellow",
          icon: createElement(AlertCircle, { size: 18 }),
          autoClose: 8000,
          withCloseButton: true,
        })
      }

      return { success: true as const }
    } catch (error: any) {
      set({ isLoading: false })

      const impact = (error as DepartmentDeactivationConflictError).impact
      if (impact) {
        return { success: false as const, impact }
      }

      const errorMessage = error.message || "Failed to deactivate department. Please try again."
      set({ error: errorMessage })

      notifications.show({
        title: "Deactivation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return { success: false as const }
    }
  },

  /**
   * Activate a department. Requires an assigned admin — the 400 otherwise
   * surfaces through the normal error/notification path.
   */
  activateDepartment: async (id: number) => {
    const department = get().departments.find((d) => d.id === id)
    const departmentName = department ? department.name : "Department"

    set({ isLoading: true, error: null })
    try {
      const result = await departmentsService.activateDepartment(id)

      set((state) => ({
        departments: state.departments.map((d) => (d.id === id ? { ...d, is_active: true } : d)),
        selectedDepartment:
          state.selectedDepartment?.id === id
            ? { ...state.selectedDepartment, is_active: true }
            : state.selectedDepartment,
        isLoading: false,
      }))

      notifications.show({
        title: "Department Activated",
        message: result.message || `${departmentName} has been activated`,
        color: "teal",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to activate department. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Activation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return false
    }
  },

  /**
   * Assign admin to a department
   */
  assignAdmin: async (departmentId: number, memberId: number) => {
    set({ isLoading: true, error: null })
    try {
      const updatedDepartment = await departmentsService.assignAdmin(departmentId, memberId)

      // Update in departments list
      set((state) => ({
        departments: state.departments.map((d) => (d.id === departmentId ? updatedDepartment : d)),
        selectedDepartment: updatedDepartment,
        isLoading: false,
      }))

      // Show success notification
      notifications.show({
        title: "Admin Assigned",
        message: "Department admin has been assigned successfully",
        color: "purple",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return updatedDepartment
    } catch (error: any) {
      const errorMessage = error.message || "Failed to assign admin. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Admin Assignment Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Remove admin from a department
   */
  removeAdmin: async (departmentId: number, adminId: number) => {
    set({ isLoading: true, error: null })
    try {
      const updatedDepartment = await departmentsService.removeAdmin(departmentId, adminId)

      // Update in departments list
      set((state) => ({
        departments: state.departments.map((d) => (d.id === departmentId ? updatedDepartment : d)),
        selectedDepartment: updatedDepartment,
        isLoading: false,
      }))

      // Show success notification
      notifications.show({
        title: "Admin Removed",
        message: "Department admin has been removed successfully",
        color: "purple",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return updatedDepartment
    } catch (error: any) {
      const errorMessage = error.message || "Failed to remove admin. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Admin Removal Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Set selected department
   */
  setSelectedDepartment: (department: Department | null) => {
    set({ selectedDepartment: department })
  },

  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null })
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState)
  },
}))
